import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, FileText, Printer, Pencil, X, Search } from 'lucide-react';
import { employeeApi, masterApi, letterRegApi } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/assets/aampl-logo.jpg';
const DEFAULT_REASON = 'Due to exigencies of service';
const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Generate Transfer Letter.
 *
 * Employees come from the employee master and units from the Unit master — the
 * page holds no roster or unit list of its own. Choosing a unit is the only
 * place a destination is entered; the subject and every unit reference in the
 * letter are derived from it. Editing the fields here shapes the letter only,
 * never the employee's master record.
 */
export default function TransferLetter() {
  const { activeUnit } = useUnit();
  const { hasPerm } = useAuth();
  const [params] = useSearchParams();

  const [emps, setEmps] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [empId, setEmpId] = useState(params.get('employee') || '');
  const [empQuery, setEmpQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [letterDate, setLetterDate] = useState(todayIso());
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [address, setAddress] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [relation, setRelation] = useState('S/o');
  // Who signs. Chosen per letter from Assistant Manager level upwards.
  const [signatories, setSignatories] = useState([]);
  const [signedBy, setSignedBy] = useState('');
  // Optional co-signatory, for letters two people sign.
  const [signedBy2, setSignedBy2] = useState('');
  const [subject, setSubject] = useState('');
  const [subjectTouched, setSubjectTouched] = useState(false);

  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const pickerRef = useRef(null);

  const canIssue = hasPerm('letters.generate');

  useEffect(() => {
    let live = true;
    Promise.all([
      employeeApi.list({ unit: activeUnit }).catch(() => ({ data: [] })),
      masterApi.units().catch(() => ({ data: [] })),
      letterRegApi.signatories().catch(() => ({ data: {} })),
    ]).then(([e, u, sg]) => {
      if (!live) return;
      setEmps(e.data || []);
      setUnits(u.data || []);
      setSignatories(sg.data?.signatories || []);
    }).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [activeUnit]);

  // Close the employee picker on an outside click.
  useEffect(() => {
    const onDown = (ev) => {
      if (pickerRef.current && !pickerRef.current.contains(ev.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const emp = useMemo(() => emps.find((e) => e.id === empId) || null, [emps, empId]);
  const unitDoc = useMemo(() => units.find((u) => u.name === toUnit) || null, [units, toUnit]);

  const matches = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    const pool = emps.filter((e) => e.status !== 'Left');
    if (!q) return pool.slice(0, 40);
    return pool.filter((e) =>
      (e.name || '').toLowerCase().includes(q) ||
      (e.card_no || '').toLowerCase().includes(q) ||
      (e.department || '').toLowerCase().includes(q) ||
      (e.designation || '').toLowerCase().includes(q)).slice(0, 40);
  }, [emps, empQuery]);

  // How the destination reads inside the letter: company name + plant address,
  // exactly as the printed letters are worded. Falls back to the unit's own
  // name when no address is configured for it yet.
  const unitFull = useMemo(() => {
    const lh = unitDoc?.letterhead;
    if (!lh) return '';
    return lh.address ? `${lh.company_name}, ${lh.address}` : lh.name;
  }, [unitDoc]);

  // The subject follows the chosen unit and reason until HR edits it by hand.
  // The reason reads mid-sentence there, so its leading capital is dropped
  // unless the word is an acronym.
  useEffect(() => {
    if (!subjectTouched) {
      const r = reason.trim() || DEFAULT_REASON;
      const inline = r.slice(0, 2) === r.slice(0, 2).toUpperCase() ? r : r[0].toLowerCase() + r.slice(1);
      setSubject(unitFull ? `Transfer ${inline} at ${unitFull}` : '');
    }
  }, [unitFull, reason, subjectTouched]);

  // Employee records carry no address field, so the last letter that did is
  // the best starting point. Purely a convenience — it stays editable, and
  // nothing here writes back to the employee record.
  const selectEmployee = async (e) => {
    setEmpId(e.id);
    setPickerOpen(false);
    setEmpQuery('');
    setPreviewHtml('');
    setRecipientName(e.name || '');
    setFromUnit(e.unit || '');
    try {
      const r = await letterRegApi.list(e.id);
      const prior = (r.data || []).find((l) => (l.address || '').trim());
      setAddress(prior ? prior.address : '');
    } catch { setAddress(''); }
  };

  const validate = () => {
    if (!emp) { toast.error('Please select an employee and transfer unit before generating the letter.'); return false; }
    if (!toUnit) { toast.error('Please select an employee and transfer unit before generating the letter.'); return false; }
    if (fromUnit && fromUnit === toUnit) {
      toast.error('The current unit and the transfer unit are the same — pick a different destination.');
      return false;
    }
    if (!effectiveDate) { toast.error('Please select the transfer effective date.'); return false; }
    return true;
  };

  const payload = () => ({
    type: 'transfer',
    to_unit: toUnit,
    from_unit: fromUnit,
    effective_date: effectiveDate,
    letter_date: letterDate,
    reason: reason.trim() || DEFAULT_REASON,
    title: subject.trim(),
    address: address.trim(),
    recipient_name: recipientName.trim(),
    relation,
    signatory_employee_id: signedBy || undefined,
    signatory_employee_id_2: signedBy2 || undefined,
    logo_url: LOGO_URL,
  });

  const preview = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const r = await letterRegApi.previewTransfer(emp.id, payload());
      setPreviewHtml(r.data.html);
      setTimeout(() => document.getElementById('tl-preview')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the preview');
    } finally { setBusy(false); }
  };

  // Files the letter, then opens it for print / save-as-PDF. Filing it is what
  // puts it on the employee's record and in Letter History.
  const issueAndOpen = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const created = await letterRegApi.add(emp.id, payload());
      const r = await letterRegApi.print(created.data.id, { logo_url: LOGO_URL });
      const w = window.open('', '_blank');
      if (w) { w.document.write(r.data.html); w.document.close(); }
      else toast.error('Allow pop-ups to open the letter');
      toast.success('Transfer letter filed on the employee record');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate the letter');
    } finally { setBusy(false); }
  };

  const reset = () => {
    setEmpId(''); setToUnit(''); setFromUnit(''); setEffectiveDate(todayIso()); setLetterDate(todayIso());
    setReason(DEFAULT_REASON); setAddress(''); setSubject(''); setSubjectTouched(false);
    setRecipientName(''); setRelation('S/o'); setSignedBy(''); setSignedBy2('');
    setPreviewHtml('');
  };

  const field = 'block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748B] mb-2';

  return (
    <div data-testid="transfer-letter-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1100px] mx-auto">
            <div className="mb-7">
              <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554] mb-2">
                Generate Transfer Letter
              </h1>
              <p className="text-sm text-[#64748B]">
                Select an employee and the unit they are being transferred to — the letter fills itself in.
              </p>
            </div>

            {!canIssue && (
              <div className="glass-card p-4 mb-6 !border-[#FDE68A]">
                <p className="text-sm text-[#B45309]">You do not have permission to issue letters.</p>
              </div>
            )}

            <div className="glass-card reveal p-5 sm:p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* -------- Employee (searchable) -------- */}
                <div className="md:col-span-2" ref={pickerRef}>
                  <Label className={field}>Employee</Label>
                  <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] z-10" strokeWidth={1.8} />
                    <Input
                      data-testid="tl-employee-search"
                      className="pl-10"
                      placeholder={loading ? 'Loading employees…' : 'Search by name, card no., department…'}
                      value={pickerOpen ? empQuery : (emp ? `${emp.name}${emp.card_no ? ` · ${emp.card_no}` : ''}` : '')}
                      onChange={(ev) => { setEmpQuery(ev.target.value); setPickerOpen(true); }}
                      onFocus={() => { setPickerOpen(true); setEmpQuery(''); }}
                    />
                    {pickerOpen && (
                      <div data-testid="tl-employee-list"
                           className="absolute z-30 mt-1 w-full max-h-[280px] overflow-auto rounded-2xl border border-white/80 bg-white/95 backdrop-blur-xl shadow-lift">
                        {matches.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-[#94A3B8]">No matching employee.</p>
                        ) : matches.map((e) => (
                          <button key={e.id} type="button"
                            data-testid={`tl-employee-${e.id}`}
                            onClick={() => selectEmployee(e)}
                            className="w-full text-left px-4 py-2.5 hover:bg-[rgba(56,189,248,0.14)] transition-colors">
                            <span className="block text-sm font-semibold text-[#172554]">{e.name}</span>
                            <span className="block text-[11px] text-[#64748B]">
                              {[e.card_no, e.designation, e.department, e.unit].filter(Boolean).join(' · ')}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {emp && (
                    <div className="mt-2.5 flex flex-wrap gap-2 text-[11px]">
                      {emp.designation && <span className="pill pill-primary">{emp.designation}</span>}
                      {emp.department && <span className="pill pill-info">{emp.department}</span>}
                      {emp.unit && <span className="pill pill-no-dot pill-ai">Currently: {emp.unit}</span>}
                    </div>
                  )}
                </div>

                {/* -------- Recipient line, as it prints -------- */}
                <div>
                  <Label className={field}>Addressed As</Label>
                  <Input data-testid="tl-recipient-name" value={recipientName}
                         onChange={(ev) => { setRecipientName(ev.target.value); setPreviewHtml(''); }}
                         placeholder="e.g. Mrs. Anju Ravish" />
                  <p className="mt-1.5 text-[11px] text-[#94A3B8]">
                    Prefilled from the employee record. Add a title here if the letter needs one —
                    the employee record is not changed.
                  </p>
                </div>
                <div>
                  <Label className={field}>Relation Line</Label>
                  <select data-testid="tl-relation" value={relation}
                          onChange={(ev) => { setRelation(ev.target.value); setPreviewHtml(''); }}
                          className="w-full h-10 px-3.5 text-sm">
                    <option value="S/o">S/o</option>
                    <option value="W/o">W/o</option>
                    <option value="D/o">D/o</option>
                    <option value="">Omit this line</option>
                  </select>
                  <p className="mt-1.5 text-[11px] text-[#94A3B8]">
                    {emp?.father_husband
                      ? <>Prints as <b className="text-[#1D4ED8]">{relation ? `${relation} ${emp.father_husband}` : '— omitted —'}</b></>
                      : 'This employee record has no father / husband name, so the line is skipped.'}
                  </p>
                </div>

                {/* -------- Signing authority -------- */}
                <div className="md:col-span-2">
                  <Label className={field}>Signing Authority</Label>
                  <select data-testid="tl-signatory" value={signedBy}
                          onChange={(ev) => { setSignedBy(ev.target.value); if (!ev.target.value) setSignedBy2(''); setPreviewHtml(''); }}
                          className="w-full h-10 px-3.5 text-sm">
                    <option value="">Select who signs this letter…</option>
                    {signatories.map((sgn) => (
                      <option key={sgn.id} value={sgn.id}>
                        {sgn.name} — {sgn.designation}{sgn.unit ? ` · ${sgn.unit}` : ''}
                      </option>
                    ))}
                  </select>
                  {signedBy && (
                    <div className="mt-3">
                      <Label className={field}>
                        Second Signature <span className="normal-case font-normal text-[#94A3B8]">(optional)</span>
                      </Label>
                      <select data-testid="tl-signatory-2" value={signedBy2}
                              onChange={(ev) => { setSignedBy2(ev.target.value); setPreviewHtml(''); }}
                              className="w-full h-10 px-3.5 text-sm">
                        <option value="">Signed by one person only</option>
                        {signatories.filter((sgn) => sgn.id !== signedBy).map((sgn) => (
                          <option key={sgn.id} value={sgn.id}>
                            {sgn.name} — {sgn.designation}{sgn.unit ? ` · ${sgn.unit}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <p className="mt-1.5 text-[11px] text-[#94A3B8]">
                    Assistant Manager level and above. Their name and designation print above the
                    signing line, so it can never be mistyped.
                    {signedBy2 && ' Two signatures print side by side.'}
                  </p>
                </div>

                {/* -------- Current Unit -------- */}
                <div>
                  <Label className={field}>Current Unit</Label>
                  <select data-testid="tl-from-unit-select" value={fromUnit}
                          onChange={(ev) => { setFromUnit(ev.target.value); setPreviewHtml(''); }}
                          className="w-full h-10 px-3.5 text-sm">
                    <option value="">Not stated in the letter</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.name}>{u.name}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-[11px] text-[#94A3B8]">
                    Where the employee works today — prefilled from their record. The letter reads
                    &ldquo;transferred from … to …&rdquo;. Leave unset to name only the destination.
                  </p>
                </div>

                {/* -------- Transfer To Unit -------- */}
                <div>
                  <Label className={field}>Transfer To Unit</Label>
                  <select data-testid="tl-unit-select" value={toUnit}
                          onChange={(ev) => { setToUnit(ev.target.value); setPreviewHtml(''); }}
                          className="w-full h-10 px-3.5 text-sm">
                    <option value="">Select unit…</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.name}>{u.name}</option>
                    ))}
                  </select>
                  {unitDoc && (
                    <p className="mt-2 text-[11px] text-[#64748B]">
                      {unitDoc.letterhead?.address
                        ? <>Appears in the letter as: <b className="text-[#1D4ED8]">{unitFull}</b></>
                        : <span className="text-[#B45309]">
                            No address configured for this unit — the letter will name the unit only.
                            Add one in Settings → Units.
                          </span>}
                    </p>
                  )}
                </div>

                {/* -------- Dates -------- */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={field}>Effective Date</Label>
                    <Input data-testid="tl-effective-date" type="date" value={effectiveDate}
                           onChange={(ev) => { setEffectiveDate(ev.target.value); setPreviewHtml(''); }} />
                  </div>
                  <div>
                    <Label className={field}>Letter Date</Label>
                    <Input data-testid="tl-letter-date" type="date" value={letterDate}
                           onChange={(ev) => { setLetterDate(ev.target.value); setPreviewHtml(''); }} />
                  </div>
                </div>

                {/* -------- Reason -------- */}
                <div className="md:col-span-2">
                  <Label className={field}>Reason</Label>
                  <Input data-testid="tl-reason" value={reason}
                         onChange={(ev) => { setReason(ev.target.value); setPreviewHtml(''); }}
                         placeholder={DEFAULT_REASON} />
                </div>

                {/* -------- Subject -------- */}
                <div className="md:col-span-2">
                  <Label className={field}>Subject</Label>
                  <Input data-testid="tl-subject" value={subject}
                         onChange={(ev) => { setSubject(ev.target.value); setSubjectTouched(true); setPreviewHtml(''); }}
                         placeholder="Select a unit to fill this in automatically" />
                  {subjectTouched && (
                    <button type="button" onClick={() => setSubjectTouched(false)}
                            className="mt-1.5 text-[11px] font-semibold text-brand-primary hover:underline underline-offset-4">
                      Reset to the automatic subject
                    </button>
                  )}
                </div>

                {/* -------- Address -------- */}
                <div className="md:col-span-2">
                  <Label className={field}>Employee Address</Label>
                  <textarea data-testid="tl-address" rows={3} value={address}
                            onChange={(ev) => { setAddress(ev.target.value); setPreviewHtml(''); }}
                            placeholder="House / street&#10;City, State"
                            className="w-full rounded-xl border border-[rgba(37,99,235,0.16)] bg-white/70 backdrop-blur-sm px-3.5 py-2.5 text-sm
                                       transition-all duration-200 placeholder:text-[#94A3B8]
                                       hover:border-[rgba(56,189,248,0.55)] focus:outline-none focus:border-brand-sky
                                       focus:ring-4 focus:ring-brand-sky/20 focus:bg-white" />
                  <p className="mt-1.5 text-[11px] text-[#94A3B8]">
                    One line per line of the address. Prefilled from this employee&apos;s most recent letter where
                    available — editing it here does not change the employee record.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button data-testid="tl-preview-btn" onClick={preview} disabled={busy || !canIssue}>
                  <FileText className="w-4 h-4" /> Preview Letter
                </Button>
                <Button variant="outline" onClick={reset} disabled={busy}>
                  <X className="w-4 h-4" /> Cancel
                </Button>
              </div>
            </div>

            {/* -------- Preview -------- */}
            {previewHtml && (
              <div id="tl-preview" className="glass-card reveal p-5 sm:p-6" data-testid="tl-preview-panel">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554]">Letter Preview</h2>
                  <div className="flex flex-wrap gap-2.5">
                    <Button variant="outline" size="sm" onClick={() => setPreviewHtml('')}>
                      <Pencil className="w-4 h-4" /> Edit
                    </Button>
                    <Button data-testid="tl-pdf-btn" size="sm" onClick={issueAndOpen} disabled={busy}>
                      <Send className="w-4 h-4" /> Generate PDF
                    </Button>
                    <Button data-testid="tl-print-btn" variant="important" size="sm" onClick={issueAndOpen} disabled={busy}>
                      <Printer className="w-4 h-4" /> Print Letter
                    </Button>
                  </div>
                </div>
                {/* Rendered in an iframe: the letter's own print CSS stays sealed
                    off from the application's, so what prints is only the letter. */}
                <iframe
                  title="Transfer letter preview"
                  data-testid="tl-preview-frame"
                  srcDoc={previewHtml}
                  className="w-full h-[760px] rounded-2xl border border-white/80 bg-white"
                />
                <p className="mt-3 text-[11px] text-[#94A3B8]">
                  This preview is not filed. Generate PDF or Print places the letter on the employee&apos;s
                  record and in Letter History.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
